import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FieldError, svgSprite, Tooltip } from '../../../../../hybrid/wrappers';
import css from './FormRadioCircles.module.scss';
import classnames from 'classnames';
import { generateCompStylers } from '../../../../../common/utils/styles';
import RadioCircle from '../RadioCircle/RadioCircle';

const FormRadioCircles = ({
    title,
    attribute,
    disabled,
    handlers = {},
    compStyle,
    value,
    setFormData,
    errors,
    options,
    required
}) => {
    const { stylers, classes } = generateCompStylers(compStyle);

    const [val, setVal] = useState(value || 0);
    const changeHandler = useCallback(
        (v, isDisabled) => {
            if (!disabled && !isDisabled) {
                setFormData(attribute, v);
                setVal(v);
                if (handlers?.onChange) handlers.onChange(attribute, v);
            }
        },
        [disabled, setFormData, attribute, handlers]
    );

    useEffect(() => {
        setVal(value);
    }, [value]);

    const checkIsActive = (item, idx) => (item?.value ? item?.value === val : Number(val) === idx && val !== null);

    return (
        <div style={stylers.wrapper} className={classnames(classes.wrapper, css.fullWidth, css.wrapper)}>
            {title?.body && (
                <div className={css.formRadioTitleWrapper}>
                    <p
                        style={stylers.title}
                        className={classnames(css.formRadioTitle, classes.title, { [css.formRadioTitleRequired]: required })}
                    >
                        {title.body}
                    </p>
                    {title?.hint && (
                        <Tooltip compStyle={{ tooltipWrap: css.formRadioTitleTooltipWrap }} text={title?.hint}>
                            {svgSprite('question-circle-fill')}
                        </Tooltip>
                    )}
                </div>
            )}
            <div style={stylers.itemWrapper} className={classnames(classes.itemWrapper, css.fullWidth)}>
                {options.map((item, idx) => (
                    <React.Fragment key={`${attribute}option${idx}`}>
                        <div
                            {...{
                                key: String(Date.now()),
                                onClick: () => changeHandler(item?.value || idx, item?.disabled)
                            }}
                            style={stylers.radio}
                            className={classnames(css.formRadio, classes.radio, {
                                [css.formRadioActive]: checkIsActive(item, idx),
                                [css.formRadioDisabled]: item?.disabled
                            })}
                        >
                            <RadioCircle isActive={checkIsActive(item, idx)} />
                            {item?.title || item}
                            {item?.hint ? (
                                <Tooltip
                                    tooltipId={`${attribute}option${idx}`}
                                    text={item?.hint}
                                    compStyle={{
                                        inner: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginLeft: '12px'
                                        }
                                    }}
                                >
                                    {svgSprite('info-circle', {
                                        style: {
                                            width: '15px',
                                            height: '15px',
                                            fill: 'var(--greyExtraDarkColor)'
                                        }
                                    })}
                                </Tooltip>
                            ) : null}
                        </div>
                    </React.Fragment>
                ))}
            </div>
            {errors && errors.length ? errors.map((err, idx) => <FieldError key={`${attribute}${idx}`}>{err}</FieldError>) : null}
        </div>
    );
};
FormRadioCircles.propTypes = {
    title: PropTypes.object,
    attribute: PropTypes.string,
    disabled: PropTypes.bool,
    handlers: PropTypes.object,
    compStyle: PropTypes.object,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setFormData: PropTypes.func,
    errors: PropTypes.array,
    options: PropTypes.array
};
export default FormRadioCircles;
